import type React from 'react';
import { useEffect, useState } from 'react';

import * as styles from './styles';
import { ONE_HOUR_IN_MILLIS } from '..';
import { TimeFilterOverlay } from './TimeFilterOverlay';
import moment from 'moment';

import type { DateRange } from '@cobliteam/common-types';

import { type CloseOverlayHandler, Dropdown, HoverButton } from '@cobliteam/base-components';
import { format } from '@cobliteam/base-components/src/Slider/TimeRangeSlider';

import Icon from '@cobliteam/ui/icons';

export interface TimeFilterProps {
  timeSelection: DateRange;
  isOpen?: boolean;
  onApply: (timeSelection: DateRange) => void;
  onClear?: () => void;
  onDropdownClick?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  popperPlacementType?: any;
}

export const DEFAULT_TIME_SELECTION: DateRange = {
  start: 1,
  end: 24 * 60 * 60 * 1000,
};

const baseTime = moment().startOf('day').valueOf();

export const TimeFilter: React.FC<TimeFilterProps> = ({ isOpen, popperPlacementType, ...props }) => {
  const [forceClosed, setForceClosed] = useState(false);
  const [selectionChanged, setSelectionChanged] = useState(false);
  const [timeSelection, setTimeSelection] = useState<DateRange>(props.timeSelection);

  useEffect(() => {
    setForceClosed(!isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (forceClosed) {
      setForceClosed(false);
    }
  }, [forceClosed]);

  const getButtonTitle = () => {
    const hasTimeSelected = timeSelection.start !== DEFAULT_TIME_SELECTION.start || timeSelection.end !== DEFAULT_TIME_SELECTION.end;
    const selectedTime = hasTimeSelected ? `(${format(timeSelection.start + baseTime)} - ${format(timeSelection.end + baseTime)})` : '';

    return `Filtrar horário ${selectedTime}`.trim();
  };

  const handleTimeSelection = (option: DateRange) => {
    setTimeSelection({
      start: option.start - baseTime,
      end: option.end - baseTime,
    });
    setSelectionChanged(true);
  };

  const clearFilters = () => {
    setTimeSelection(DEFAULT_TIME_SELECTION);
    setSelectionChanged(true);

    if (props.onClear) {
      props.onClear();
    }
  };

  const onCancel = () => {
    setForceClosed(true);
    setTimeSelection(props.timeSelection);

    if (props.onCancel) {
      props.onCancel();
    }
  };

  const applyFilter = () => {
    props.onApply(timeSelection);

    setForceClosed(true);
    setSelectionChanged(false);
  };

  const onClose = () => {
    setSelectionChanged(false);
    if (props.onClose) {
      props.onClose();
    }
  };

  return (
    <>
      <Dropdown
        keepOpen
        forceClosed={forceClosed}
        onClick={props.onDropdownClick}
        onClose={onClose}
        placement={popperPlacementType}
        overlay={(closeOverlayHandler: CloseOverlayHandler) => (
          <TimeFilterOverlay
            timeSelection={timeSelection}
            overlayTitle='Intervalo mínimo de 6 h'
            selectionChanged={selectionChanged}
            onTimeSelection={handleTimeSelection}
            onClearFilter={clearFilters}
            onApplyFilter={applyFilter}
            onCancel={onCancel}
            closeOverlayHandler={closeOverlayHandler}
            sliderMinValue={baseTime + DEFAULT_TIME_SELECTION.start}
            sliderMaxValue={baseTime + DEFAULT_TIME_SELECTION.end}
            sliderLeftValue={baseTime + timeSelection.start}
            sliderRightValue={baseTime + timeSelection.end}
            step={ONE_HOUR_IN_MILLIS}
            minInterval={6 * ONE_HOUR_IN_MILLIS}
          />
        )}
      >
        <HoverButton hovered={isOpen}>
          <>
            <div className={styles.icon}>
              <Icon name='hourglass' />
            </div>
            {getButtonTitle()}
          </>
        </HoverButton>
      </Dropdown>
    </>
  );
};
